
import { useTranslations } from "use-intl";
import { LayoutDashboard, FileText, Trophy, CreditCard, User, BarChart3, MessageCircle, Gamepad2, ListChecks, BookOpenCheck } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/utils/cn";

/**
 * Kept short on purpose (owner, 2026-10-26: "iloji boricha qisqartirish kerak"): one "Mocklar" entry (the skill
 * and "Yaqinda tushgan" filters live on that page), one "Statistika" entry (Streak and Yutuqlar are tabs there),
 * no Friday entry (Friday's button sits in the bottom-right corner of every page), no section headings.
 */
const TELEGRAM_SUPPORT_URL = "https://t.me/examora_support_bot";

function NavLink({
  href,
  labelKey,
  Icon,
  isActive,
  t,
}: {
  href: string;
  labelKey: string;
  Icon: typeof LayoutDashboard;
  isActive: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <Link
      href={href}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {t(labelKey)}
    </Link>
  );
}

export function StudentSidebar() {
  const t = useTranslations("exams");
  const pathname = usePathname();

  // `lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)]` (top-16/4rem matches
  // SiteHeader's own sticky h-16): without this, the parent row
  // (WithSidebarLayout) only sets `min-h-[calc(100vh-4rem)]`, so on a
  // page whose main content is taller than the viewport (e.g. /pricing
  // with several plan cards) the flex row — and this <aside> stretching
  // to match it via default `align-items: stretch` — grows to that
  // content's full height. The ACCOUNT block below is pinned to the
  // bottom of <nav> via `mt-auto`, so it ended up rendered far down the
  // page, well below the fold, instead of staying visible in the
  // sidebar. Pinning the sidebar itself to the viewport (and letting its
  // own <nav> scroll internally via `overflow-y-auto` if its content
  // ever exceeds that height) keeps Narxlar/Profil always in view.
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card/50 lg:sticky lg:top-16 lg:flex lg:h-[calc(100vh-4rem)]">
      <nav className="flex flex-1 flex-col gap-6 overflow-y-auto p-3">
        <div className="flex flex-col gap-1">
          <NavLink href="/dashboard" labelKey="nav.dashboard" Icon={LayoutDashboard} isActive={pathname === "/dashboard"} t={t} />
          <NavLink href="/plan" labelKey="nav.plan" Icon={ListChecks} isActive={pathname === "/plan"} t={t} />
          <NavLink href="/exams" labelKey="nav.mobile.mocks" Icon={FileText} isActive={pathname === "/exams"} t={t} />
          <NavLink href="/vocabulary" labelKey="nav.vocabulary" Icon={BookOpenCheck} isActive={pathname === "/vocabulary"} t={t} />
          <NavLink href="/ranking" labelKey="nav.ranking" Icon={Trophy} isActive={pathname === "/ranking"} t={t} />
          <NavLink
            href="/games"
            labelKey="nav.games"
            Icon={Gamepad2}
            isActive={pathname.startsWith("/games") || pathname === "/challenges"}
            t={t}
          />
          <NavLink
            href="/stats"
            labelKey="nav.stats"
            Icon={BarChart3}
            isActive={pathname === "/stats" || pathname === "/streak" || pathname === "/achievements"}
            t={t}
          />
        </div>

        <div className="mt-auto flex flex-col gap-1 border-t border-border pt-3">
          <NavLink href="/pricing" labelKey="nav.pricing" Icon={CreditCard} isActive={pathname === "/pricing"} t={t} />
          <NavLink href="/profile" labelKey="nav.profile" Icon={User} isActive={pathname === "/profile"} t={t} />
          {/* External link (the Telegram support bot), not an internal
              route -- a plain <a>, not NavLink's i18n-aware <Link>. */}
          <a
            href={TELEGRAM_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {t("nav.support")}
          </a>
        </div>
      </nav>
    </aside>
  );
}
